import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

interface Brand {
  id: string;
  name: string;
  description: string | null;
  industry: string | null;
  website: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  typography: string | null;
  style_guide_url: string | null;
  brand_assets: string[] | null;
  brand_colors: string[] | null;
  brand_fonts: string[] | null;
  brand_guidelines: string[] | null;
  created_at: string;
  updated_at: string;
}

const Brands: React.FC = () => {
  const { user } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    industry: '',
    website: '',
    primary_color: '#3B82F6',
    secondary_color: '#EF4444',
    typography: '',
    brand_assets: [] as string[],
    brand_colors: ['#3B82F6', '#EF4444'] as string[],
    brand_fonts: ['Inter'] as string[],
    brand_guidelines: [] as string[]
  });
  const [editLogo, setEditLogo] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchBrands();
  }, [user]);

    const fetchBrands = async () => {
    if (!user) return;
      setLoading(true);
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .eq('user_id', user.id);
      if (!error) setBrands(data || []);
      setLoading(false);
    };

  const handleBrandClick = (brand: Brand) => {
    setSelectedBrand(brand);
    setEditForm({
      name: brand.name,
      description: brand.description || '',
      industry: brand.industry || '',
      website: brand.website || '',
      primary_color: brand.primary_color || '#3B82F6',
      secondary_color: brand.secondary_color || '#EF4444',
      typography: brand.typography || '',
      brand_assets: brand.brand_assets || [],
      brand_colors: brand.brand_colors || ['#3B82F6', '#EF4444'],
      brand_fonts: brand.brand_fonts || ['Inter'],
      brand_guidelines: brand.brand_guidelines || []
    });
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      name: selectedBrand?.name || '',
      description: selectedBrand?.description || '',
      industry: selectedBrand?.industry || '',
      website: selectedBrand?.website || '',
      primary_color: selectedBrand?.primary_color || '#3B82F6',
      secondary_color: selectedBrand?.secondary_color || '#EF4444',
      typography: selectedBrand?.typography || '',
      brand_assets: selectedBrand?.brand_assets || [],
      brand_colors: selectedBrand?.brand_colors || ['#3B82F6', '#EF4444'],
      brand_fonts: selectedBrand?.brand_fonts || ['Inter'],
      brand_guidelines: selectedBrand?.brand_guidelines || []
    });
    setEditLogo(null);
    setError(null);
    setSuccess(null);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file for the logo');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }
      setEditLogo(file);
      setError(null);
    }
  };

  const handleSave = async () => {
    if (!selectedBrand) return;
    
    setSaving(true);
    setError(null);
    
    let logoUrl = selectedBrand.logo_url; // Keep existing logo by default
    
    // Upload new logo if one was selected
    if (editLogo) {
      setUploadingLogo(true);
      const { data: logoData, error: logoError } = await supabase.storage
        .from('brand-logos')
        .upload(`logos/${Date.now()}_${editLogo.name}`, editLogo);
      
      if (logoError) {
        setError('Logo upload failed: ' + logoError.message);
        setSaving(false);
        setUploadingLogo(false);
        return;
      }
      
      logoUrl = logoData?.path ? supabase.storage.from('brand-logos').getPublicUrl(logoData.path).data.publicUrl : null;
      setUploadingLogo(false);
    }
    
    // Update the brand with all fields including new logo
    const { error: updateError } = await supabase
      .from('brands')
      .update({
        name: editForm.name,
        description: editForm.description,
        industry: editForm.industry,
        website: editForm.website,
        primary_color: editForm.primary_color,
        secondary_color: editForm.secondary_color,
        typography: editForm.typography,
        logo_url: logoUrl,
        updated_at: new Date().toISOString()
      })
      .eq('id', selectedBrand.id);
    
    if (updateError) {
      setError('Failed to update brand: ' + updateError.message);
      setSaving(false);
      return;
    }
    
    setSuccess('Brand updated successfully!');
    setIsEditing(false);
    setSaving(false);
    setEditLogo(null);
    
    // Refresh the brands list
    await fetchBrands();
    
    // Update the selected brand with new data
    setSelectedBrand({
      ...selectedBrand,
      ...editForm,
      logo_url: logoUrl || selectedBrand.logo_url,
      updated_at: new Date().toISOString()
    });
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDelete = async () => {
    if (!selectedBrand) return;
    
    setSaving(true);
    setError(null);
    
    const { error: deleteError } = await supabase
      .from('brands')
      .delete()
      .eq('id', selectedBrand.id);
    
    if (deleteError) {
      setError('Failed to delete brand: ' + deleteError.message);
      setSaving(false);
      return;
    }
    
    setIsModalOpen(false);
    setSelectedBrand(null);
    setShowDeleteConfirm(false);
    await fetchBrands();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBrand(null);
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setError(null);
    setSuccess(null);
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-primary to-primary-dark rounded-2xl mb-6 shadow-xl">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Your Brands</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Manage and organize your brand identities in one place
            </p>
          </div>

          {/* Action Bar */}
      <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Brand Portfolio</h2>
                <p className="text-gray-600">Select a brand to view details or make changes</p>
              </div>
            </div>
        <Link
          to="/dashboard/brands/create"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl font-semibold hover:from-primary-dark hover:to-primary transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
          Create Brand
        </Link>
      </div>
          
      {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">Loading your brands...</p>
              </div>
            </div>
      ) : brands.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-3xl mb-6">
                <span className="text-4xl">🏢</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No brands found</h3>
              <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                Get started by creating your first brand identity to use across all your projects.
              </p>
              <Link
                to="/dashboard/brands/create"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl font-semibold text-lg hover:from-primary-dark hover:to-primary transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Create Your First Brand
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {brands.map((brand) => (
            <div
              key={brand.id}
                  onClick={() => handleBrandClick(brand)}
                  className="group bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 hover:scale-105"
                >
                  {/* Brand Logo/Initials */}
                  <div className="flex items-center justify-center w-20 h-20 rounded-2xl font-bold text-2xl mb-6 mx-auto bg-gradient-to-r from-blue-100 to-blue-200 text-blue-600 group-hover:from-blue-200 group-hover:to-blue-300 transition-all duration-300 overflow-hidden">
                    {brand.logo_url ? (
                      <img 
                        src={brand.logo_url} 
                        alt={`${brand.name} logo`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      brand.name?.slice(0, 2).toUpperCase()
                    )}
                  </div>
                  
                  {/* Brand Info */}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary transition-colors mb-2">
                      {brand.name}
                    </h3>
                    <p className="text-gray-600 line-clamp-2 mb-4">
                      {brand.description || 'No description provided'}
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      {brand.industry || 'No industry specified'}
                    </div>
                  </div>
                  
                  {/* Brand Colors Preview */}
                  {(brand.primary_color || brand.secondary_color) && (
                    <div className="flex items-center justify-center space-x-2 mb-6">
                      {brand.primary_color && (
                        <div 
                          className="w-8 h-8 rounded-full border-2 border-white shadow-md" 
                          style={{ backgroundColor: brand.primary_color }}
                          title={`Primary: ${brand.primary_color}`}
                        ></div>
                      )}
                      {brand.secondary_color && (
                        <div 
                          className="w-8 h-8 rounded-full border-2 border-white shadow-md" 
                          style={{ backgroundColor: brand.secondary_color }}
                          title={`Secondary: ${brand.secondary_color}`}
                        ></div>
                      )}
                    </div>
                  )}
                  
                  {/* Action Indicator */}
                  <div className="flex items-center justify-center text-gray-400 group-hover:text-primary transition-colors">
                    <span className="text-sm font-medium mr-2">View Details</span>
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Brand Details Modal */}
      {isModalOpen && selectedBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary via-primary-dark to-primary px-8 py-8 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">
                      {selectedBrand.name?.slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">{selectedBrand.name}</h2>
                    <p className="text-red-100">Brand Details & Management</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-xl"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              {error && (
                <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-red-800">Update Error</h4>
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {success && (
                <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-800">Success!</h4>
                      <p className="text-green-700">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              {isEditing ? (
                /* Edit Form */
                <div className="space-y-8">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <label className="block text-lg font-semibold text-gray-900 mb-3">Brand Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg placeholder-gray-400"
                        required
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <label className="block text-lg font-semibold text-gray-900 mb-3">Industry</label>
                      <input
                        type="text"
                        value={editForm.industry}
                        onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                        className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg placeholder-gray-400"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-3">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={4}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg placeholder-gray-400 resize-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-3">Website</label>
                    <input
                      type="url"
                      value={editForm.website}
                      onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg placeholder-gray-400"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <label className="block text-lg font-semibold text-gray-900">Primary Color</label>
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <input
                            type="color"
                            value={editForm.primary_color}
                            onChange={(e) => setEditForm({ ...editForm, primary_color: e.target.value })}
                            className="w-16 h-16 border-2 border-gray-200 rounded-2xl cursor-pointer shadow-lg"
                          />
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full border-2 border-gray-200 flex items-center justify-center">
                            <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={editForm.primary_color}
                            onChange={(e) => setEditForm({ ...editForm, primary_color: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-mono text-lg"
                            placeholder="#3B82F6"
                          />
                          <p className="text-gray-500 text-sm mt-1">Main brand color</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <label className="block text-lg font-semibold text-gray-900">Secondary Color</label>
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <input
                            type="color"
                            value={editForm.secondary_color}
                            onChange={(e) => setEditForm({ ...editForm, secondary_color: e.target.value })}
                            className="w-16 h-16 border-2 border-gray-200 rounded-2xl cursor-pointer shadow-lg"
                          />
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full border-2 border-gray-200 flex items-center justify-center">
                            <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </div>
              </div>
              <div className="flex-1">
                          <input
                            type="text"
                            value={editForm.secondary_color}
                            onChange={(e) => setEditForm({ ...editForm, secondary_color: e.target.value })}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-mono text-lg"
                            placeholder="#EF4444"
                          />
                          <p className="text-gray-500 text-sm mt-1">Accent color</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-3">Typography</label>
                    <input
                      type="text"
                      value={editForm.typography}
                      onChange={(e) => setEditForm({ ...editForm, typography: e.target.value })}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg placeholder-gray-400"
                      placeholder="e.g., Inter, Roboto, Helvetica Neue"
                    />
                  </div>

                  {/* Logo Upload */}
                  <div className="space-y-4">
                    <label className="block text-lg font-semibold text-gray-900 mb-3">Brand Logo</label>
                    <div className="space-y-4">
                      {/* Current Logo Preview */}
                      {selectedBrand.logo_url && (
                        <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-2xl border border-gray-200">
                          <img 
                            src={selectedBrand.logo_url} 
                            alt={`${selectedBrand.name} logo`}
                            className="w-16 h-16 object-contain bg-white rounded-xl border border-gray-200 shadow-sm"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">Current logo</p>
                            <p className="text-xs text-gray-500">Click below to replace</p>
                          </div>
                        </div>
                      )}
                      
                      {/* New Logo Upload */}
                      <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-primary transition-all duration-300 hover:bg-gray-50/50">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="edit-logo-upload"
                        />
                        <label htmlFor="edit-logo-upload" className="cursor-pointer block">
                          <div className="mx-auto w-16 h-16 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload New Logo</h4>
                          <p className="text-gray-600 mb-4">PNG, JPG, or GIF • Max 5MB</p>
                          <div className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Choose File
                          </div>
                        </label>
                      </div>
                      
                      {editLogo && (
                        <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-xl border border-green-200">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-green-800 font-medium">New logo selected: {editLogo.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-8">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Description
                      </h3>
                      <p className="text-gray-900 font-medium">{selectedBrand.description || 'No description provided'}</p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Industry
                      </h3>
                      <p className="text-gray-900 font-medium">{selectedBrand.industry || 'Not specified'}</p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        Website
                      </h3>
                      {selectedBrand.website ? (
                        <a 
                          href={selectedBrand.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary-dark underline font-medium"
                        >
                          {selectedBrand.website}
                        </a>
                      ) : (
                        <p className="text-gray-900 font-medium">Not specified</p>
                      )}
                    </div>
                    
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Created
                      </h3>
                      <p className="text-gray-900 font-medium">{new Date(selectedBrand.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  {/* Visual Identity Section */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mr-4">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                        </svg>
                      </div>
                      Visual Identity
                    </h3>
                    
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                      {/* Logo */}
                      {selectedBrand.logo_url && (
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                          <h4 className="text-sm font-medium text-gray-500 mb-4">Brand Logo</h4>
                          <div className="flex items-center space-x-4">
                            <img 
                              src={selectedBrand.logo_url} 
                              alt={`${selectedBrand.name} logo`}
                              className="w-20 h-20 object-contain bg-white rounded-2xl border border-gray-200 shadow-sm"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Brand logo</p>
                              <a 
                                href={selectedBrand.logo_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary-dark text-sm underline"
                              >
                                View full size
                              </a>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Colors */}
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                        <h4 className="text-sm font-medium text-gray-500 mb-4">Brand Colors</h4>
                        <div className="space-y-4">
                          <div className="flex items-center space-x-4">
                            <div 
                              className="w-12 h-12 rounded-2xl border-2 border-white shadow-lg" 
                              style={{ backgroundColor: selectedBrand.primary_color || '#3B82F6' }}
                            ></div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Primary</p>
                              <p className="text-xs text-gray-500 font-mono">{selectedBrand.primary_color || '#3B82F6'}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div 
                              className="w-12 h-12 rounded-2xl border-2 border-white shadow-lg" 
                              style={{ backgroundColor: selectedBrand.secondary_color || '#EF4444' }}
                            ></div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Secondary</p>
                              <p className="text-xs text-gray-500 font-mono">{selectedBrand.secondary_color || '#EF4444'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Typography */}
                      {selectedBrand.typography && (
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                          <h4 className="text-sm font-medium text-gray-500 mb-4">Typography</h4>
                          <p className="text-sm text-gray-900 font-mono">{selectedBrand.typography}</p>
                        </div>
                      )}

                      {/* Style Guide */}
                      {selectedBrand.style_guide_url && (
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                          <h4 className="text-sm font-medium text-gray-500 mb-4">Style Guide</h4>
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-white rounded-2xl border border-gray-200 flex items-center justify-center shadow-sm">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Brand style guide</p>
                              <a 
                                href={selectedBrand.style_guide_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary-dark text-sm underline"
                              >
                                View document
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-8 border-t border-gray-200 bg-gray-50 rounded-b-3xl">
              <div className="flex space-x-4">
                {!isEditing && (
                  <button
                    onClick={handleEdit}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Brand
                  </button>
                )}
                
                {isEditing && (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Save Changes
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={handleCancelEdit}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={saving}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Brand
                </button>
                
                <button
                  onClick={closeModal}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-red-100 to-red-200 rounded-2xl mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Confirm Deletion</h2>
              <p className="text-gray-700 mb-8 text-lg">
                Are you sure you want to delete the brand <span className="font-semibold text-gray-900">"{selectedBrand.name}"</span>? 
                <br />
                <span className="text-red-600 font-medium">This action cannot be undone.</span>
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800 rounded-xl hover:from-gray-400 hover:to-gray-500 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Brand
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Brands; 